/* eslint-disable no-console */
/**
 * Basemap Debug Utility
 *
 * Centralized debug logging control for basemap-related functionality.
 * Set REACT_APP_BASEMAP_DEBUG=false in environment to disable debug logs and LayerList widget.
 *
 * Usage:
 * import { debugLog, isBasemapDebugEnabled } from 'utilities/basemap-debug.util';
 *
 * debugLog('[Component]', 'Message', data);
 * if (isBasemapDebugEnabled()) { ... }
 *
 * Runtime Override (only works if REACT_APP_BASEMAP_DEBUG=true):
 * window.enableBasemapDebug(true)  // Enable debug
 * window.enableBasemapDebug(false) // Disable debug
 */

// SessionStorage key for runtime override
const BASEMAP_DEBUG_OVERRIDE_KEY = 'basemap-debug-override';

/**
 * Get runtime override from sessionStorage
 */
function getRuntimeOverride(): boolean | null {
  try {
    const stored = sessionStorage.getItem(BASEMAP_DEBUG_OVERRIDE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Set runtime override in sessionStorage
 */
function setRuntimeOverride(value: boolean | null): void {
  try {
    if (value === null) {
      sessionStorage.removeItem(BASEMAP_DEBUG_OVERRIDE_KEY);
    } else {
      sessionStorage.setItem(BASEMAP_DEBUG_OVERRIDE_KEY, String(value));
    }
  } catch (e) {
    // Ignore storage errors
  }
}

/**
 * Check if basemap debug mode is enabled
 * @returns boolean - True if debug mode is enabled
 */
export function isBasemapDebugEnabled(): boolean {
  const debugEnv = process.env.REACT_APP_BASEMAP_DEBUG;

  // Check if environment variable allows debug mode
  const envAllowsDebug = debugEnv && debugEnv.toLowerCase() === 'true';

  // If env doesn't allow debug, always return false (production)
  if (!envAllowsDebug) {
    return false;
  }

  // If env allows debug, check runtime override from sessionStorage
  const runtimeOverride = getRuntimeOverride();
  if (runtimeOverride !== null) {
    return runtimeOverride;
  }

  // Default to FALSE when env allows it (disabled by default in non-prod)
  // User must explicitly enable via window.enableBasemapDebug(true)
  return false;
}

/**
 * Runtime function to enable/disable debug mode from browser console
 * Only works when REACT_APP_BASEMAP_DEBUG=true
 * @param enabled - True to enable debug, false to disable
 */
export function enableBasemapDebug(enabled: boolean): void {
  const debugEnv = process.env.REACT_APP_BASEMAP_DEBUG;
  const envAllowsDebug = debugEnv && debugEnv.toLowerCase() === 'true';

  if (!envAllowsDebug) {
    console.warn(
      '[Basemap Debug] Cannot override debug mode. REACT_APP_BASEMAP_DEBUG must be set to "true" in environment.'
    );
    return;
  }

  // Store in sessionStorage so it persists across page reloads
  setRuntimeOverride(enabled);

  console.log(
    '[Basemap Debug] Debug mode ' + (enabled ? 'enabled' : 'disabled')
  );
  console.log(
    '[Basemap Debug] Reload the page to see LayerList widget changes.'
  );
}

// Expose to window for console access (only in non-prod environments)
if (typeof window !== 'undefined') {
  const debugEnv = process.env.REACT_APP_BASEMAP_DEBUG;
  const envAllowsDebug = debugEnv && debugEnv.toLowerCase() === 'true';

  if (envAllowsDebug) {
    // Only expose function in non-production environments
    // @ts-ignore - Dynamic property assignment
    window.enableBasemapDebug = enableBasemapDebug;
  }
  // In production, function is not exposed to window at all
}

/**
 * Log a debug message if debug mode is enabled
 * @param message - Primary message to log
 * @param optionalParams - Additional parameters to log
 */
export function debugLog(message?: any, ...optionalParams: any[]): void {
  if (isBasemapDebugEnabled()) {
    if (optionalParams.length > 0) {
      console.log(message, ...optionalParams);
    } else {
      console.log(message);
    }
  }
}

/**
 * Log a debug warning if debug mode is enabled
 * @param message - Primary message to log
 * @param optionalParams - Additional parameters to log
 */
export function debugWarn(message?: any, ...optionalParams: any[]): void {
  if (isBasemapDebugEnabled()) {
    if (optionalParams.length > 0) {
      console.warn(message, ...optionalParams);
    } else {
      console.warn(message);
    }
  }
}

/**
 * Log a debug error (always logs regardless of debug mode)
 * @param message - Primary message to log
 * @param optionalParams - Additional parameters to log
 */
export function debugError(message?: any, ...optionalParams: any[]): void {
  // Always log errors regardless of debug mode
  if (optionalParams.length > 0) {
    console.error(message, ...optionalParams);
  } else {
    console.error(message);
  }
}
